import { FC, ReactNode, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import { useHistory, useLocation } from 'react-router-dom'
import {
  Drawer,
  List,
  Typography,
  Divider,
  Box,
  useTheme,
  IconButton,
  Stack,
  Button,
  Tooltip,
} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import * as CONFIG from '@/config'
import CommonService from '@/services/CommonService'
import AgentsService from '@/services/AgentsService'
import { useGlobalContext } from '@/context/GlobalContext'
import {
  Channels,
  Dashboard,
  Form,
  Knowledge,
  Maximize,
  Question,
  Quiz,
  Star,
  Users,
} from '../UI/Icons'
import { CustomLink } from './CustomLink'
import logoHead from '@/assets/images/logo-head.svg'
import Logo from '@/assets/New-images/logo.svg'

const commonService = new CommonService()
const agentService = new AgentsService()

interface SidebarItem {
  icon: ReactNode
  label: string
  link: string
}

const windowObj: any = window

interface SidebarProps {
  isCollapsed: boolean
  toggleCollapse: () => void
}

const Sidebar: FC<SidebarProps> = ({ isCollapsed, toggleCollapse }) => {
  const { t } = useTranslation('translation')
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const { setSelectedAgentName } = useGlobalContext()
  const theme = useTheme()

  const { data: orgData } = useQuery({
    queryKey: ['all-agents'],
    queryFn: async () => {
      const response = await agentService.getCurrentOrganisation()

      return response?.data?.data?.organisation || {}
    },
  })

  useEffect(() => {
    const selectedAgent = localStorage.getItem('selectedAgent')
    const searchParams = new URLSearchParams(location.search)

    // const urlAgentId = searchParams.get('agent')
    // if (urlAgentId && urlAgentId !== currentAgent) {
    //   handleAgentUpdate(urlAgentId)
    // } else
    if (selectedAgent && !searchParams.has('agent')) {
      searchParams.append('agent', selectedAgent)
      history.replace({ search: searchParams.toString() })
    }
  }, [location, history])

  const categories: {
    category: string
    items: SidebarItem[]
  }[] = [
    {
      category: '#',
      items: [
        {
          icon: <Dashboard />,
          label: t('Dashboard'),
          link: '/dashboard',
        },
      ],
    },
    {
      category: t('Set Up'),
      items: [
        {
          icon: <Knowledge />,
          label: t('Knowledge'),
          link: CONFIG.KNOWLEDGE_ALL,
        },
        {
          icon: <Quiz />,
          label: t('Activities'),
          link: CONFIG.EXERCISES,
        },
        {
          icon: <Form />,
          label: t('Forms'),
          link: '/forms',
        },
      ],
    },
    {
      category: t('Manage'),
      items: [
        {
          icon: <Channels />,
          label: t('Channels'),
          link: '/chat-customisation?viewMode=true',
        },
        {
          icon: <Users />,
          label: t('Users'),
          link: `${CONFIG.USER_MANAGEMENT}?viewMode=true`,
        },
      ],
    },
  ]

  return (
    <Box
      sx={{
        width: isCollapsed ? 70 : 200,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      <IconButton
        onClick={toggleCollapse}
        sx={{
          position: 'absolute',
          // display: { xs: 'none', md: 'flex' },
          display: 'flex',
          right: -15,
          top: 20,
          backgroundColor: 'white',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          border: '1px solid #eaeaea',
          width: 30,
          height: 30,
          '&:hover': {
            backgroundColor: '#f5f5f5',
          },
          zIndex: 9999,
        }}
      >
        {isCollapsed ? (
          <ChevronRightIcon fontSize="small" />
        ) : (
          <ChevronLeftIcon fontSize="small" />
        )}
      </IconButton>

      <Box
        sx={{
          p: isCollapsed ? 1 : 2,
          display: 'flex',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
        }}
      >
        <img
          src={isCollapsed ? logoHead : Logo}
          alt="Logo"
          style={{ maxWidth: isCollapsed ? '40px' : '100%' }}
        />
      </Box>

      <Stack flexGrow={1} sx={{ overflow: 'auto', gap: 2 }}>
        {categories.map(({ category, items }, categoryIndex) =>
          category === '#' ? (
            <List key={categoryIndex} disablePadding>
              {items.map((item, index) => (
                <CustomLink key={index} {...item} isCollapsed={isCollapsed} />
              ))}
            </List>
          ) : (
            <Box key={categoryIndex}>
              {!isCollapsed && (
                <Typography
                  variant="subtitle2"
                  sx={{
                    px: 2,
                    color: '#79767A',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  }}
                >
                  {category}
                </Typography>
              )}
              <List disablePadding sx={{ mt: 0.5 }}>
                {items.map((item, index) => (
                  <CustomLink key={index} {...item} isCollapsed={isCollapsed} />
                ))}
              </List>
            </Box>
          ),
        )}
      </Stack>

      <Tooltip title={isCollapsed ? t('Learner View') : ''} placement="right">
        <Button
          variant="contained"
          // href={
          //   orgData?.enable_homepage
          //     ? `${windowObj.CHATBOT_URL}/home?through=admin-portal`
          //     : `${windowObj.CHATBOT_URL}?agent=${localStorage.getItem(
          //         'selectedAgent',
          //       )}&through=admin-portal`
          // }
          href={`${windowObj.CHATBOT_URL}?agent=${localStorage.getItem(
            'selectedAgent',
          )}&through=admin-portal`}
          target="_blank"
          sx={{
            mx: 0.5,
            minHeight: 40,
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            px: isCollapsed ? 0 : 2,
            width: 'calc(100% - 8px)',
            textTransform: 'none',
            fontWeight: 700,
            fontFamily: 'Mulish, sans-serif',
            fontSize: '0.875rem',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
              color: 'white',
            },
          }}
        >
          {isCollapsed ? (
            <Maximize />
          ) : (
            <>
              <Box component="span" sx={{ mr: 1.5, display: 'flex' }}>
                <Maximize />
              </Box>
              {t('Learner View')}
            </>
          )}
        </Button>
      </Tooltip>

      <Divider sx={{ pt: 2 }} />

      <Box sx={{ py: 2 }}>
        <CustomLink
          icon={<Star />}
          label={t("What's New")}
          // onClick={() => handleWhatsNewClick()}
          link="https://noodlefactory-support.notion.site/New-Releases-247a6979da7e80a3a505e9189a3b1e6a"
          isExternal
          isCollapsed={isCollapsed}
        />
        <CustomLink
          icon={<Question />}
          label={t('Support')}
          link={CONFIG.SUPPORT}
          isCollapsed={isCollapsed}
        />
      </Box>
    </Box>
  )
}

interface CustomSidebarProps {
  // isShown: boolean
  // onToggle: () => void
  isCollapsed: boolean
  toggleCollapse: () => void
}

const CustomSidebar: FC<CustomSidebarProps> = ({
  isCollapsed,
  toggleCollapse,
}) => {
  // const [showsWhatsNew, setShowsWhatsNew] = useState<boolean>(false)
  const theme = useTheme()

  const getStates = async () => {
    const resp: any = await commonService.getUserStates()
    const { status, data } = resp
    if (status === 200) {
      if (data?.data?.states) {
        const states = data.data.states
        if (states?.showsWhatsNew === true) {
          // setShowsWhatsNew(true)
          commonService.updateUserStates({
            states: {
              showsWhatsNew: false,
            },
          })
        }
      }
    }
  }

  useEffect(() => {
    getStates()
  }, [])

  return (
    <>
      {/* Desktop Drawer - persistent */}
      <Drawer
        variant="permanent"
        sx={{
          // display: { xs: 'none', md: 'block' },
          display: 'block',
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: isCollapsed ? 70 : 200,
            boxShadow: '0 0 10px rgba(0,0,0,0.1)',
            border: 'none',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflow: 'visible',
          },
        }}
        open
      >
        <Sidebar
          // handleWhatsNewClick={handleWhatsNewClick}
          isCollapsed={isCollapsed}
          toggleCollapse={() => {
            toggleCollapse()
          }}
        />
      </Drawer>

      {/* <WhatsNewPopup show={showsWhatsNew} onHide={hideWhatsNewPopup} /> */}
    </>
  )
}

export default CustomSidebar
